import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Modal from '../../components/Modal';
import ScreenViewService from '../../components/ScreenViewService';
import StoreService from '../../components/StoreService';
import { toast } from 'react-toastify';

function ScreenViewsView() {
  const empty = {
    store_ids: [],
    order_status: [],
    views: [],
    expiry_date: '',
  };
  const [screenViews, setScreenViews] = useState([]);
  const [formData, setFormData] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [stores, setStores] = useState([]);

  const fetchData = async () => {
    try {
      const [screenViewsData, storesData] = await Promise.all([
        ScreenViewService.fetchScreenViews(),
        StoreService.fetchStores(),
      ]);
      setScreenViews(screenViewsData.screenViews || screenViewsData.response || screenViewsData.data || screenViewsData || []);
      setStores(storesData.response || storesData.data || storesData || []);
    } catch (error) {
      toast.error('Erreur lors de la récupération des screen views');
      console.error('Error fetching screen views:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const storeOptions = useMemo(
    () =>
      Array.isArray(stores)
        ? stores.map((store) => ({
            value: store._id || store.id,
            label: store.name || store.storeCode || store.city || store._id || store.id,
          }))
        : [],
    [stores]
  );

  const storeLabelMap = useMemo(() => {
    const map = {};
    storeOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, [storeOptions]);

  const ORDER_STATUS_OPTIONS = [
    { value: 'pending', label: 'pending' },
    { value: 'accepted', label: 'accepted' },
    { value: 'ready', label: 'ready' },
    { value: 'gone', label: 'gone' },
  ];

  const handleExpiryChange = (e) => {
    setFormData({ ...formData, expiry_date: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.store_ids.length) {
      toast.error('Sélectionnez au moins un magasin');
      return;
    }
    try {
      const payload = {
        store_ids: formData.store_ids,
        order_status: formData.order_status,
        views: formData.views,
        expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null,
      };
      if (editingId) {
        await ScreenViewService.updateScreenView(editingId, payload);
        toast.success('Screen view modifié avec succès');
      } else {
        await ScreenViewService.createScreenView(payload);
        toast.success('Screen view créé avec succès');
      }
      setFormData({ ...empty });
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error('Error saving screen view:', error);
    }
  };

  const handleEdit = (screenView) => {
    setEditingId(screenView._id || screenView.id);
    setFormData({
      store_ids: screenView.store_ids || [],
      order_status: screenView.order_status || [],
      views: screenView.views || [],
      expiry_date: formatDateTimeLocal(screenView.expiry_date),
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Voulez-vous vraiment supprimer ce screen view ?')) {
      try {
        await ScreenViewService.deleteScreenView(id);
        toast.success('Screen view supprimé avec succès');
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
        console.error('Error deleting screen view:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR');
  };

  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const tzOffset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - tzOffset * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  return (
    <div className="container mt-4">
      <h2>Gestion des Screen Views</h2>
      <div className="text-end mb-3">
        <button
          className="btn btn-success"
          data-bs-toggle="modal"
          data-bs-target="#screenViewModal"
          onClick={() => {
            setFormData({ ...empty });
            setEditingId(null);
          }}
        >
          Ajouter un Screen View
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-bordered table-striped">
          <thead>
            <tr>
              <th>Magasins</th>
              <th>Statuts commande</th>
              <th>Vues</th>
              <th>Code écran</th>
              <th>Token</th>
              <th>Expiration</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(screenViews) && screenViews.map((screenView) => (
              <tr key={screenView._id || screenView.id}>
                <td>{(screenView.store_ids || []).map((id) => storeLabelMap[id] || id).join(', ')}</td>
                <td>
                  {(screenView.order_status || []).map((status) => (
                    <span key={status} className="badge bg-primary me-1">{status}</span>
                  ))}
                </td>
                <td>{(screenView.views || []).join(', ')}</td>
                <td>{screenView.screen_code}</td>
                <td className="text-break" style={{ maxWidth: '200px' }}>{screenView.token_jwt}</td>
                <td>{formatDate(screenView.expiry_date)}</td>
                <td>{formatDate(screenView.last_login_at)}</td>
                <td>
                  <button
                    className="btn btn-sm btn-warning me-1"
                    data-bs-toggle="modal"
                    data-bs-target="#screenViewModal"
                    onClick={() => handleEdit(screenView)}
                  >
                    Modifier
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(screenView._id || screenView.id)}
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        modalId="screenViewModal"
        title={editingId ? 'Modifier un Screen View' : 'Ajouter un Screen View'}
        onSubmit={handleSubmit}
        submitLabel={editingId ? 'Modifier' : 'Ajouter'}
      >
        <form>
          <div className="mb-3">
            <label className="form-label">Magasins</label>
            <Select
              isMulti
              options={storeOptions}
              value={storeOptions.filter((option) => formData.store_ids.includes(option.value))}
              onChange={(selected) => setFormData({ ...formData, store_ids: selected.map((opt) => opt.value) })}
              placeholder="Sélectionnez les magasins"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Statuts commande</label>
            <Select
              isMulti
              options={ORDER_STATUS_OPTIONS}
              value={ORDER_STATUS_OPTIONS.filter((option) => formData.order_status.includes(option.value))}
              onChange={(selected) => setFormData({ ...formData, order_status: selected.map((opt) => opt.value) })}
              placeholder="Choisissez les statuts"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Vues</label>
            <CreatableSelect
              isMulti
              value={(formData.views || []).map((view) => ({ value: view, label: view }))}
              onChange={(selected) => setFormData({ ...formData, views: selected.map((opt) => opt.value) })}
              placeholder="Ajouter des vues et appuyer sur Entrée"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Date d'expiration</label>
            <input
              className="form-control"
              type="datetime-local"
              value={formData.expiry_date}
              onChange={handleExpiryChange}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default ScreenViewsView;
